import styles from '../styles.module.scss';
import ExpandBtn from './expand-btn';
import { Labours } from './labours';
import { Parts } from './parts';
import {
  PartsStatus,
  VHCRecordCheckBrakeItemTypesVHCRecordLabourDto,
  VHCRecordCheckBrakeItemTypesVHCRecordPartDto,
  VHCRecordCheckItemTypesVHCRecordLabourDto,
  VHCRecordCheckItemTypesVHCRecordPartDto,
  VHCRecordCheckWheelTyreItemTypesVHCRecordLabourDto,
  VHCRecordCheckWheelTyreItemTypesVHCRecordPartDto
} from 'api/generated';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { useAuth } from 'features/auth/contexts/auth';
import { useCommonRepairOrderQuotesQueryKey } from 'features/vhc/api/use-common-repair-order-quotes.query';
import { useVhcRecordsRepairOrderQuoteMutation } from 'features/vhc/api/use-vhc-records-repair-order-quote.mutation';
import { useVhcRecordsRepairPartsMutation } from 'features/vhc/api/use-vhc-records-repair-parts.mutation';
import { useRecordStore } from 'features/vhc/model';
import { useVhcStore } from 'features/vhc/model/vhc-store';
import { FC, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useCollapse } from 'react-collapsed';
import { useQueryClient } from 'react-query';
import { formatDate } from 'utils/formats';

interface LaboursTabProps {
  labours:
    | (
        | VHCRecordCheckBrakeItemTypesVHCRecordLabourDto
        | VHCRecordCheckItemTypesVHCRecordLabourDto
        | VHCRecordCheckWheelTyreItemTypesVHCRecordLabourDto
      )[]
    | null
    | undefined;
  parts:
    | (
        | VHCRecordCheckWheelTyreItemTypesVHCRecordPartDto
        | VHCRecordCheckBrakeItemTypesVHCRecordPartDto
        | VHCRecordCheckItemTypesVHCRecordPartDto
      )[]
    | null
    | undefined;
  recordId: number;
  partRequestStatus?: PartsStatus | null;
  userCreatePartRequest?: string | null;
  dateCreatePartRequest?: string | null;
  onLookUpClick?: () => void;
  onCreatePartRequest?: () => void;
  refetch?: () => void;
  isRepairOrdersCreated: boolean;
  dateCreateRepairOrderQuote?: string | null;
  userCreateRepairOrderQuote?: string | null;
  isComplete?: boolean;
}
export const LaboursAndPartsTab: FC<LaboursTabProps> = ({
  labours,
  parts,
  recordId,
  onLookUpClick,
  partRequestStatus,
  userCreatePartRequest,
  dateCreatePartRequest,
  refetch,
  isRepairOrdersCreated,
  dateCreateRepairOrderQuote,
  userCreateRepairOrderQuote,
  isComplete
}: LaboursTabProps) => {
  const { currentUser } = useAuth();
  const [isExpandedLabours, setExpandedLabours] = useState(false);
  const { getCollapseProps: getCollapsePropsLabours, getToggleProps: getTogglePropsLabours } = useCollapse({
    isExpanded: isExpandedLabours
  });

  const [isExpandedParts, setExpandedParts] = useState(false);
  const { getCollapseProps: getCollapsePropsParts, getToggleProps: getTogglePropsParts } = useCollapse({
    isExpanded: isExpandedParts
  });

  const [totalLaboursTime, setTotalLaboursTime] = useState(0);

  const { setRecordModalIsOpen, filter } = useVhcStore();

  const { items } = useRecordStore();

  function handleViewOpen(id: number) {
    const correctItem = items?.find((i) => i.id === id);
    if (correctItem) {
      setRecordModalIsOpen({ data: correctItem, isComplete: isComplete }, true);
    }
  }

  const queryClient = useQueryClient();

  const { mutate: createQuote, isLoading: isLoadingCreateQuote } = useVhcRecordsRepairOrderQuoteMutation();
  const { mutate: createParts, isLoading: isLoadingPartsCreate } = useVhcRecordsRepairPartsMutation();

  const handleCreateQuote = () => {
    createQuote(
      {
        id: Number(recordId),
        userId: currentUser?.userId
      },
      {
        onSuccess: () => {
          if (refetch) refetch();
          queryClient.invalidateQueries({ queryKey: useCommonRepairOrderQuotesQueryKey });
        }
      }
    );
  };

  const handleCreateParts = () => {
    createParts(
      {
        id: Number(recordId),
        userId: currentUser?.userId
      },
      {
        onSuccess() {
          if (refetch) refetch();
        }
      }
    );
  };

  useEffect(() => {
    if (labours) {
      const sumTime = labours.map((time) => time.time).reduce((partialSum, a) => partialSum + a, 0);
      setTotalLaboursTime(sumTime);
    }
  }, [labours]);

  const isDisabledPartsRequest: boolean =
    Boolean(isComplete) && Boolean(parts?.length) && Boolean(!dateCreateRepairOrderQuote);

  return (
    <>
      <div>
        <div>
          <div className={clsx('d-flex flex-column flex-sm-row gap-4 justify-content-start', styles.tabBtn)}>
            <span className="d-flex flex-sm-row flex-column gap-3 align-items-sm-center align-items-start">
              {isRepairOrdersCreated && (
                <div className={clsx('text-start', styles.createQuoteInfo)}>
                  Created {dateCreateRepairOrderQuote ? formatDate(dateCreateRepairOrderQuote) : '-'}
                  <br />
                  by {userCreateRepairOrderQuote?.length ? userCreateRepairOrderQuote : '-'}
                </div>
              )}
              <button
                disabled={isRepairOrdersCreated || isLoadingCreateQuote}
                type="button"
                onClick={handleCreateQuote}
                className={clsx('btn btn-primary btn-sm align-self-sm-start', styles.createQuoteBtn)}
              >
                Create Quote
              </button>
            </span>
            <span className="d-flex justify-content-start">
              <button onClick={onLookUpClick} className={`${styles['lookUpButton']} ${styles['buttonTextStyle']}`}>
                <span>
                  <InlineSvg path="/media/svg/vhc/labours-parts-circle.svg" className="svg-icon-1" />
                </span>
                Update
              </button>
            </span>
          </div>
        </div>
        <div>
          <ExpandBtn
            isExpanded={isExpandedLabours}
            getToggleProps={getTogglePropsLabours}
            setExpanded={setExpandedLabours}
            label={
              <>
                Labours{' '}
                <span className="text-muted fw-bold ms-3">{`${
                  !totalLaboursTime ? 0 : (totalLaboursTime / 60).toFixed(2)
                } hours`}</span>
              </>
            }
          />
        </div>
        <div {...getCollapsePropsLabours()}>
          {isExpandedLabours && <Labours data={{ items: labours }} handleViewOpen={handleViewOpen} />}
        </div>
        <div>
          <ExpandBtn
            isExpanded={isExpandedParts}
            getToggleProps={getTogglePropsParts}
            setExpanded={setExpandedParts}
            label={<>Parts</>}
          />
        </div>
        <div {...getCollapsePropsParts()}>
          {isExpandedParts && <Parts data={{ items: parts }} handleViewOpen={handleViewOpen} />}
        </div>
        {
          <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center mt-5 justify-content-between">
            {partRequestStatus ? (
              <div className="d-flex flex-column flex-md-row gap-2 align-items-start align-items-md-center">
                <div className="mb-2 mb-md-0">
                  <Button disabled className={`${styles.createdPartsBtn} ${styles.buttonTextStyle}`}>
                    Part Request Created
                  </Button>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="fw-semibold text-muted fs-8">Status:</span>
                  <span
                    className="d-flex justify-content-center align-items-center input-group-text"
                    style={{
                      color: 'white',
                      background: '#3B74CE',
                      borderRadius: 4,
                      width: 91,
                      height: 24
                    }}
                  >
                    {PartsStatus[partRequestStatus]}
                  </span>
                  <span className="fw-semibold text-muted fs-8">
                    Created: {dateCreatePartRequest && formatDate(dateCreatePartRequest, 'DD/MM/YYYY HH:MM')} - by{' '}
                    {userCreatePartRequest}
                  </span>
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2">
                <Button
                  disabled={!isDisabledPartsRequest || isLoadingPartsCreate}
                  className={`${styles.createPartsBtn} ${styles.buttonTextStyle}`}
                  onClick={handleCreateParts}
                >
                  Create Part Request
                </Button>
                {!isDisabledPartsRequest && (
                  <span className="fw-semibold text-muted fs-8">VHC record must be completed</span>
                )}
              </div>
            )}
          </div>
        }
      </div>
    </>
  );
};
